import { useState, useRef } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { motion, useInView } from 'motion/react';
import { projectStages } from '../data/projectStages';
import { config } from '../config/config';
import { validateForm, isFormValid, type FormErrors, type FormData } from '../utils/formValidation';
import { Turnstile } from '@marsidev/react-turnstile';

export function Contact() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    stage: '',
    message: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validateForm(formData);
    setErrors(validationErrors);
    // Mark all fields as touched to show errors
    const allTouched: Record<string, boolean> = {};
    Object.keys(formData).forEach((key) => (allTouched[key] = true));
    setTouched(allTouched);

    if (!isFormValid(validationErrors)) {
      toast.error('Por favor, corrige los errores del formulario.');
      return;
    }
    
    if (!captchaToken) {
      toast.error('Por favor, completa el captcha de seguridad.');
      return;
    }

    setIsSubmitting(true);
    try {
      // Attempt to send to API if configured, otherwise simulate
      if (config.api.baseUrl) {
        const response = await fetch(`${config.api.baseUrl}${config.api.contactFormEndpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...formData, captcha: captchaToken }),
        });
        if (!response.ok) throw new Error('Error al enviar');
      } else {
        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }

      toast.success('¡Mensaje enviado! Nos pondremos en contacto pronto.', {
        icon: <CheckCircle className="h-5 w-5 text-green-500" />,
      });
      setFormData({ name: '', email: '', phone: '', company: '', stage: '', message: '' });
      setErrors({});
      setTouched({});
    } catch {
      toast.error('Error al enviar el mensaje. Inténtalo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error on change if field was touched
    if (errors[name as keyof FormErrors]) {
      setErrors({ ...errors, [name]: undefined });
    }
  };

  const handleBlur = (fieldName: string) => {
    setFocusedField(null);
    setTouched({ ...touched, [fieldName]: true });
    // Validate single field on blur
    const fieldErrors = validateForm(formData);
    if (fieldErrors[fieldName as keyof FormErrors]) {
      setErrors({ ...errors, [fieldName]: fieldErrors[fieldName as keyof FormErrors] });
    }
  };

  const getFieldClass = (fieldName: keyof FormErrors) => {
    const hasError = touched[fieldName] && errors[fieldName];
    return hasError ? 'border-red-400 focus-visible:border-red-500 focus-visible:ring-red-500/50' : 'border-slate-200';
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Teléfono',
      value: config.contact.phone,
      link: `tel:${config.contact.phone.replace(/\s/g, '')}`,
    },
    {
      icon: Mail,
      title: 'Email',
      value: config.contact.email,
      link: `mailto:${config.contact.email}`,
    },
    {
      icon: MapPin,
      title: 'Dirección',
      value: config.contact.address,
      link: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(config.contact.address)}`,
      external: true,
    },
  ];

  return (
    <section id="contact" className="py-20 md:py-32 bg-white border-t border-slate-200" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            className="inline-flex items-center px-4 py-2 rounded-full border border-primary/10 bg-gradient-to-br from-white to-slate-50 text-slate-700 mb-6 shadow-sm font-medium text-sm"
          >
            <Mail className="h-4 w-4 mr-2 text-secondary" />
            Contacto
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl text-slate-950 mb-5 tracking-tight font-bold"
          >
            Hablemos de tu Proyecto
          </motion.h2>

        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
          >
            <div className="rounded-[1.5rem] border border-slate-200 bg-slate-950 text-white shadow-sm overflow-hidden h-full">
              <div className="p-8 md:p-10">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-white/8 border border-white/10 text-slate-200 text-sm mb-6">
                  Información de Contacto
                </div>

                <div className="space-y-4">
                  {contactInfo.map((info, index) => (
                    <motion.a
                      key={index}
                      initial={{ opacity: 0, x: -16 }}
                      animate={isInView ? { opacity: 1, x: 0 } : {}}
                      transition={{ delay: 0.25 + index * 0.08 }}
                      whileHover={{ x: 6 }}
                      href={info.link}
                      target={info.external ? '_blank' : undefined}
                      rel={info.external ? 'noreferrer' : undefined}
                      className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/8 transition-colors"
                    >
                      <div className="w-12 h-12 rounded-xl bg-white text-primary flex items-center justify-center flex-shrink-0">
                        <info.icon className="h-6 w-6" />
                      </div>
                      <div>
                        <div className="text-sm text-slate-400 mb-1">{info.title}</div>
                        <div className="text-white font-medium">{info.value}</div>
                      </div>
                    </motion.a>
                  ))}
                </div>

                <div className="mt-8 pt-8 border-t border-white/10">
                  <p className="text-sm uppercase tracking-[0.22em] text-slate-400 mb-3">Horario de atención</p>
                  <div className="space-y-2 text-slate-200">
                    <div>{config.contact.hoursWeekdays}</div>
                    <div>{config.contact.hoursSaturday}</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-8 md:p-10 shadow-sm">
              <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                    Nombre Completo *
                  </label>
                  <motion.div animate={focusedField === 'name' ? { scale: 1.01 } : { scale: 1 }}>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('name')}
                      onBlur={() => handleBlur('name')}
                      placeholder="Tu nombre"
                      className={`w-full bg-white ${getFieldClass('name')}`}
                      aria-invalid={!!errors.name}
                      aria-describedby={errors.name ? 'name-error' : undefined}
                    />
                  </motion.div>
                  {touched.name && errors.name && (
                    <p id="name-error" className="mt-1 text-sm text-red-500" role="alert">{errors.name}</p>
                  )}
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                      Email *
                    </label>
                    <motion.div animate={focusedField === 'email' ? { scale: 1.01 } : { scale: 1 }}>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('email')}
                        onBlur={() => handleBlur('email')}
                        placeholder="tu@email.com"
                        className={`w-full bg-white ${getFieldClass('email')}`}
                        aria-invalid={!!errors.email}
                        aria-describedby={errors.email ? 'email-error' : undefined}
                      />
                    </motion.div>
                    {touched.email && errors.email && (
                      <p id="email-error" className="mt-1 text-sm text-red-500" role="alert">{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                      Teléfono
                    </label>
                    <motion.div animate={focusedField === 'phone' ? { scale: 1.01 } : { scale: 1 }}>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('phone')}
                        onBlur={() => handleBlur('phone')}
                        placeholder="+1 (555) 000-0000"
                        className={`w-full bg-white ${getFieldClass('phone')}`}
                        aria-invalid={!!errors.phone}
                        aria-describedby={errors.phone ? 'phone-error' : undefined}
                      />
                    </motion.div>
                    {touched.phone && errors.phone && (
                      <p id="phone-error" className="mt-1 text-sm text-red-500" role="alert">{errors.phone}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-slate-700 mb-2">
                    Empresa
                  </label>
                  <motion.div animate={focusedField === 'company' ? { scale: 1.01 } : { scale: 1 }}>
                    <Input
                      id="company"
                      name="company"
                      type="text"
                      value={formData.company}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('company')}
                      onBlur={() => handleBlur('company')}
                      placeholder="Nombre de tu empresa"
                      className="w-full bg-white border-slate-200"
                    />
                  </motion.div>
                </div>

                <div>
                  <label htmlFor="stage" className="block text-sm font-medium text-slate-700 mb-2">
                    Etapa de interes
                  </label>
                  <motion.div animate={focusedField === 'stage' ? { scale: 1.01 } : { scale: 1 }}>
                    <select
                      id="stage"
                      name="stage"
                      value={formData.stage}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('stage')}
                      onBlur={() => handleBlur('stage')}
                      className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800 shadow-xs focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] outline-none transition-colors"
                    >
                      <option value="">Selecciona una etapa</option>
                      {projectStages.map((stage) => (
                        <option key={stage.id} value={stage.shortName}>
                          {stage.shortName} - {stage.period}
                        </option>
                      ))}
                    </select>
                  </motion.div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                    Mensaje *
                  </label>
                  <motion.div animate={focusedField === 'message' ? { scale: 1.01 } : { scale: 1 }}>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('message')}
                      onBlur={() => handleBlur('message')}
                      placeholder="Cuéntanos sobre tu proyecto..."
                      rows={6}
                      className={`w-full resize-none bg-white ${getFieldClass('message')}`}
                      aria-invalid={!!errors.message}
                      aria-describedby={errors.message ? 'message-error' : undefined}
                    />
                  </motion.div>
                  {touched.message && errors.message && (
                    <p id="message-error" className="mt-1 text-sm text-red-500" role="alert">{errors.message}</p>
                  )}
                </div>

                <div className="flex justify-center my-4">
                  <Turnstile
                    siteKey="0x4AAAAAADPTzYQmoh-DqiGW"
                    onSuccess={setCaptchaToken}
                    onError={() => setCaptchaToken(null)}
                    onExpire={() => setCaptchaToken(null)}
                  />
                </div>

                <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    className="w-full bg-primary hover:bg-primary/90 text-white shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        Enviar Mensaje
                        <Send className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>
                </motion.div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
